import PlanChange from "../PlanChange";
import Model from "../Model";
import ModelStore from "../../stores/ModelStore";
import Schedule, {Frequency} from "../Schedule";
import Expense, {ExpenseCategory} from "../Expense";
import Income, {IncomeType} from "../Income";
import Duration, {DurationUnits} from "../Duration"
import {getISODateFromDate, getISODateTime} from "../../stores/StoreUtilities";
import {addMonths, addYears} from "date-fns";

export enum LTCStrategyType {
  None = "None",
  SelfInsure = "SelfInsure",
  LTCInsurance = "LTCInsurance"
}



export interface ILongTermCarePlan {
  ownerId: string
  strategyType: LTCStrategyType
  careStart?: string
  careDuration?: Duration
  careAmount?: number
  careFrequency?: Frequency
  careAnnualInf?: number
  assetId?: string
  premiumAmount?: number
  premiumStart?: string
  premiumFrequency?: Frequency
  premiumAnnualInf?: number
  premiumWaiver?: boolean
  benefitAmount?: number
  benefitFrequency?: Frequency
  benefitAnnualInf?: number
  benefitDuration?: Duration
  benefitCOLA?: boolean         // Deprecated
  benefitWaitingPeriod?: number
}

export interface ILongTermCareStrategyChange {
  plans: ILongTermCarePlan[]
}


class LongTermCareStrategyChange extends PlanChange implements ILongTermCareStrategyChange {
  plans: ILongTermCarePlan[]

  constructor (data: any) {
    super(data)
    this.plans = this.details ? JSON.parse(this.details)?.plans ?? [] : []
  }

  apply(model: Model) {
    const isoDateTime = getISODateTime()
    this.plans.forEach((p: ILongTermCarePlan) => {
      const owner = model.getOwner(p.ownerId)
      if (owner) {
        let careEnd : string | undefined = undefined
        if (p.strategyType !== LTCStrategyType.None && p.careStart && p.careAmount && p.careFrequency) {
          if (p.careDuration) {
            const startDate = model.getDate(p.careStart)
            if (startDate) {
              if (p.careDuration.units === DurationUnits.Months) {
                const endDate = addMonths(startDate, p.careDuration.amount)
                careEnd = getISODateFromDate(endDate)
              } else if (p.careDuration.units === DurationUnits.Years) {
                const endDate = addYears(startDate, p.careDuration.amount)
                careEnd = getISODateFromDate(endDate)
              } else if (p.careDuration.units === DurationUnits.Lifetime) {
                careEnd = getISODateFromDate(owner.getDateOnAge(owner.getLifeExpectancyYears()))
              }
            }
          }
          const schedule = Schedule.getDefaultSchedule(p.careFrequency ?? "Monthly")
          const careExpense = new Expense({
            id: `${p.ownerId}-ltc`,
            createdAt: isoDateTime,
            updatedAt: isoDateTime,
            accountId: model.accountId,
            userId: model.userId,
            modelId: model.id,
            model: model,
            expenseCategory: ExpenseCategory.InsuranceAndMedical,
            description: `${owner.nickname}'s LTC Expenses`,
            amount: p.careAmount,
            annualInf: p.careAnnualInf,
            discretionary: false,
            assetId: p.assetId,
            ownerId: p.ownerId,
            owner: owner,
            schedule: schedule,
            start: p.careStart,
            end: careEnd,
          })
          model.expenses.push(careExpense)
          model.addLock(careExpense.id, "*", "Locked by Long Term Care Strategy")
        }
        if (p.strategyType === LTCStrategyType.LTCInsurance) {
          const schedule = Schedule.getDefaultSchedule(p.premiumFrequency ?? "Monthly")
          const insExpense = new Expense({
            id: `${p.ownerId}-ltc-ins`,
            createdAt: isoDateTime,
            updatedAt: isoDateTime,
            accountId: model.accountId,
            userId: model.userId,
            modelId: model.id,
            model: model,
            expenseCategory: ExpenseCategory.InsuranceAndMedical,
            description: `${owner.nickname}'s LTC Insurance`,
            amount: p.premiumAmount,
            annualInf: p.premiumAnnualInf,
            discretionary: false,
            assetId: p.assetId,
            ownerId: p.ownerId,
            owner: owner,
            schedule: schedule,
            start: p.premiumStart,
            end: p.premiumWaiver ? p.careStart : undefined
          })
          model.expenses.push(insExpense)
          model.addLock(insExpense.id, "*", "Locked by Long Term Care Strategy")

          if (p.careAmount && p.careFrequency && p.benefitAmount && p.benefitDuration) {
            let benefitEnd : string | undefined = undefined
            if (p.benefitDuration) {
              const startDate = model.getDate(p.careStart)
              if (startDate) {
                if (p.benefitDuration.units === DurationUnits.Months) {
                  const endDate = addMonths(startDate, p.benefitDuration.amount)
                  benefitEnd = getISODateFromDate(endDate)
                } else if (p.benefitDuration.units === DurationUnits.Years) {
                  const endDate = addYears(startDate, p.benefitDuration.amount)
                  benefitEnd = getISODateFromDate(endDate)
                } else { // Lifetime
                  benefitEnd = careEnd
                }
                if (careEnd && benefitEnd) {
                  // if careEnd < benefitEnd then use careEnd
                  if (benefitEnd.localeCompare(careEnd) > 0) {
                    benefitEnd = careEnd
                  }
                }
              }
            }
            const schedule = Schedule.getDefaultSchedule(p.benefitFrequency ?? "Monthly")
            let benefitAnnualInf = 0
            if (p.benefitAnnualInf) {
              if (p.careAnnualInf) {
                benefitAnnualInf = Math.min(p.careAnnualInf, p.benefitAnnualInf)
              } else {
                benefitAnnualInf = Math.min(model.getDefaultInflationRate("expense"), p.benefitAnnualInf)
              }
            }
            const benefitIncome = new Income({
              id: `${p.ownerId}-ltc-benefit`,
              createdAt: isoDateTime,
              updatedAt: isoDateTime,
              accountId: model.accountId,
              userId: model.userId,
              modelId: model.id,
              model: model,
              incomeType: IncomeType.None,
              description: `${owner.nickname}'s LTC Benefit`,
              amount: Math.min(p.benefitAmount, p.careAmount),
              annualInf: benefitAnnualInf,
              ownerId: p.ownerId,
              owner: owner,
              schedule: schedule,
              start: p.careStart,
              end: benefitEnd
            })
            model.incomes.push(benefitIncome)
            model.addLock(benefitIncome.id, "*", "Locked by Long Term Care Strategy")
          }
        }
      }
    })
  }

  async commit(model: Model, modelStore: ModelStore) {
    // TODO
  }
}

export default LongTermCareStrategyChange